import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button, Modal, Switch } from "antd";

import { NavigationPath } from "../../utils";
import {
  CONSENT_SETTINGS_EVENT,
  ConsentDecision,
  readConsent,
  saveConsent,
} from "../../utils/consent";

// ============================================
// COOKIE CONSENT BANNER (Google Consent Mode v2)
//
// Rendered as a sibling of the router in App.tsx — deliberately NOT through a
// context provider, so changing consent never re-renders the registration
// wizard (Register/View.tsx manipulates #monri-button-container directly).
//
// Only the denied-by-default state matters before the user decides; that lives
// in public/index.html. This component just collects the decision.
// ============================================

/**
 * Visitor-facing routes only. Deliberately an allowlist: the admin panel and the
 * QR check-in screen must never be covered by the banner, and a future admin
 * route should not opt itself in by accident.
 */
const CONSENT_ROUTES: string[] = [
  NavigationPath.REGISTER_ROUTE,
  NavigationPath.IMPRESSUM,
  NavigationPath.PAYMENT_SECURITY,
  NavigationPath.PRIVACY_STATEMENT,
  NavigationPath.PURCHASE_TERMS,
];

function isConsentRoute(pathname: string): boolean {
  const normalized = pathname.replace(/\/+$/, "").toLowerCase() || "/";
  return CONSENT_ROUTES.includes(normalized);
}

/**
 * True while the Monri lightbox or any antd modal is on screen.
 *
 * The banner sits below antd's z-index (1000), but on small viewports the Monri
 * lightbox fills the screen and a bottom bar can still cover its controls — so
 * the banner steps aside entirely while a payment overlay is open.
 */
function useOverlayOpen(): boolean {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    let frame = 0;

    const check = () => {
      frame = 0;

      // A closed antd modal keeps its wrapper in the DOM, so the class alone is
      // not enough — go by the computed display instead.
      const modalOpen = Array.prototype.some.call(
        document.querySelectorAll(".ant-modal-wrap"),
        (element: Element) =>
          window.getComputedStyle(element).display !== "none"
      );

      setIsOpen(modalOpen || !!document.querySelector('iframe[src*="monri"]'));
    };

    const schedule = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(check);
    };

    check();
    const observer = new MutationObserver(schedule);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style", "src"],
    });

    return () => {
      observer.disconnect();
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  return isOpen;
}

const CookieConsent = () => {
  const location = useLocation();
  const overlayOpen = useOverlayOpen();

  const [decision, setDecision] = useState<ConsentDecision | null>(() =>
    readConsent()
  );
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  // Reopened later from the footer link, after a decision has been made
  useEffect(() => {
    const onOpenSettings = () => {
      const saved = readConsent();
      setAnalytics(saved?.analytics ?? false);
      setMarketing(saved?.marketing ?? false);
      setIsSettingsOpen(true);
    };

    window.addEventListener(CONSENT_SETTINGS_EVENT, onOpenSettings);
    return () =>
      window.removeEventListener(CONSENT_SETTINGS_EVENT, onOpenSettings);
  }, []);

  const decide = (acceptAnalytics: boolean, acceptMarketing: boolean) => {
    setDecision(saveConsent(acceptAnalytics, acceptMarketing));
    setIsSettingsOpen(false);
  };

  const openSettings = () => {
    setAnalytics(decision?.analytics ?? false);
    setMarketing(decision?.marketing ?? false);
    setIsSettingsOpen(true);
  };

  if (!isConsentRoute(location.pathname)) return null;

  const showBanner = !decision && !overlayOpen && !isSettingsOpen;
  if (!showBanner && !isSettingsOpen) return null;

  const primaryButton =
    "text-white bg-custom-pink-900 border border-custom-pink-900 h-10 font-bold rounded-[2px] shadow";
  const secondaryButton =
    "text-custom-gray-800 bg-white border border-custom-gray-400 h-10 font-bold rounded-[2px]";

  return (
    <>
      {showBanner && (
        <div
          role="region"
          aria-label="Kolačići"
          className="fixed bottom-0 left-0 right-0 z-[900] bg-white border-t-2 border-custom-pink-900 shadow-[0_-2px_10px_rgba(0,0,0,0.15)]"
        >
          <div className="max-w-[1200px] m-auto px-5 py-4 flex flex-col lg:flex-row lg:items-center gap-4">
            <div className="text-[13px] sm:text-[14px] text-custom-gray-800 flex-grow">
              Koristimo kolačiće koji su neophodni za rad stranice, a uz vašu
              saglasnost i one za statistiku i marketing. Bez saglasnosti se ne
              postavlja nijedan neobavezni kolačić. Više u{" "}
              <Link
                to={NavigationPath.PRIVACY_STATEMENT}
                className="underline text-custom-pink-900"
              >
                Izjavi o privatnosti
              </Link>
              .
            </div>

            <div className="flex flex-col sm:flex-row gap-3 shrink-0">
              <Button
                htmlType="button"
                onClick={openSettings}
                className={secondaryButton}
              >
                POSTAVKE
              </Button>
              <Button
                htmlType="button"
                onClick={() => decide(false, false)}
                className={secondaryButton}
              >
                ODBIJ NEOBAVEZNE
              </Button>
              <Button
                htmlType="button"
                onClick={() => decide(true, true)}
                className={primaryButton}
              >
                PRIHVATI SVE
              </Button>
            </div>
          </div>
        </div>
      )}

      <Modal
        title="Postavke kolačića"
        open={isSettingsOpen}
        onCancel={() => setIsSettingsOpen(false)}
        footer={null}
        destroyOnClose
      >
        <div className="flex flex-col gap-5 pt-2">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="font-bold text-custom-gray-800">Neophodni</div>
              <div className="text-[13px] text-custom-gray-600">
                Potrebni za rad registracije i plaćanja. Uvijek su aktivni i ne
                mogu se isključiti.
              </div>
            </div>
            <Switch checked disabled aria-label="Neophodni kolačići" />
          </div>

          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="font-bold text-custom-gray-800">Statistika</div>
              <div className="text-[13px] text-custom-gray-600">
                Anonimna statistika posjeta koja nam pomaže da poboljšamo
                stranicu.
              </div>
            </div>
            <Switch
              checked={analytics}
              onChange={setAnalytics}
              aria-label="Statistika"
            />
          </div>

          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="font-bold text-custom-gray-800">Marketing</div>
              <div className="text-[13px] text-custom-gray-600">
                Mjerenje uspješnosti oglasa i prikaz relevantnijih oglasa.
              </div>
            </div>
            <Switch
              checked={marketing}
              onChange={setMarketing}
              aria-label="Marketing"
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-end pt-2">
            <Button
              htmlType="button"
              onClick={() => decide(false, false)}
              className={secondaryButton}
            >
              ODBIJ NEOBAVEZNE
            </Button>
            <Button
              htmlType="button"
              onClick={() => decide(analytics, marketing)}
              className={primaryButton}
            >
              SAČUVAJ IZBOR
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default CookieConsent;
